import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  anchorMetricsFromRect,
  computePopoverLayout,
  panelDimensionsFromElement,
  PopoverLayout,
} from './popoverPlacement';
import { SourceFavicon } from './SourceFavicon';
import { SourceFaviconStack } from './SourceFaviconStack';
import { useHoverZoneDismiss } from './useHoverZoneDismiss';
import {
  SourceRowLink,
  SourceRowSubtitle,
  SourceRowTitle,
  SourceRowTitleLine,
  SourcesFooterTrigger,
  SourcesFooterWrapper,
  SourcesPopoverPanel,
} from './webSearch.styles';
import { MessageSource } from './webSearch.types';

interface SourcesFooterProps {
  sources: MessageSource[];
}

const DEFAULT_PANEL_LAYOUT: PopoverLayout = {
  placement: 'above',
  top: 0,
  left: 0,
  maxHeight: 320,
};

const getHostname = (url: string): string => {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
};

export const SourcesFooter = ({ sources }: SourcesFooterProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [panelLayout, setPanelLayout] = useState<PopoverLayout>(DEFAULT_PANEL_LAYOUT);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const getAnchorRect = useCallback(() => triggerRef.current?.getBoundingClientRect() ?? null, []);

  useHoverZoneDismiss({
    isOpen,
    onClose: () => setIsOpen(false),
    getAnchorRect,
    panelRef,
  });

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current || !panelRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const { height: panelHeight, width: panelWidth } = panelDimensionsFromElement(panelRef.current);
    setPanelLayout(computePopoverLayout(anchorMetricsFromRect(triggerRect), panelHeight, panelWidth, 'above'));
  }, [isOpen, sources.length]);

  if (sources.length === 0) return null;

  const handleTriggerMouseEnter = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPanelLayout({ ...DEFAULT_PANEL_LAYOUT, top: rect.top, left: rect.left });
    }
    setIsOpen(true);
  };

  return (
    <SourcesFooterWrapper>
      <SourcesFooterTrigger
        ref={triggerRef}
        type="button"
        aria-expanded={isOpen}
        onMouseEnter={handleTriggerMouseEnter}
      >
        <SourceFaviconStack sources={sources} label={t('aiInsights.sourcesCount', { count: sources.length })} />
      </SourcesFooterTrigger>
      <SourcesPopoverPanel
        ref={panelRef}
        $isOpen={isOpen}
        $top={panelLayout.top}
        $left={panelLayout.left}
        $placement={panelLayout.placement}
        $maxHeight={panelLayout.maxHeight}
      >
        {sources.map((source: MessageSource) => (
          <SourceRowLink key={source.url} href={source.url} target="_blank" rel="noopener noreferrer">
            <SourceRowTitleLine>
              <SourceFavicon url={source.url} title={source.title} />
              <SourceRowTitle>{source.title}</SourceRowTitle>
            </SourceRowTitleLine>
            <SourceRowSubtitle>{getHostname(source.url)}</SourceRowSubtitle>
          </SourceRowLink>
        ))}
      </SourcesPopoverPanel>
    </SourcesFooterWrapper>
  );
};
